// Tap away from a window to close it; tap another HUD button to switch to it in one tap
// (owner 2026-10-20: "if I have Znajomi open and I click on Rynek the Znajomi should just close
// and Rynek open").
//
// - trackPanel(scene, id, close, { dom }): the window that is open now (Rynek, Schowek, Znajomi,
//   Ustawienia). `dom` = a page panel beside the canvas with no backdrop (Znajomi): a tap on the
//   game closes it, and that tap does nothing else.
// - switchButton(scene, button, id, fn): a HUD button that can be tapped "through" a window.
//   Its onClick runs fn only when nothing is open; with something open the scene's tap handler
//   closes the window first and then runs fn, unless it was that button's own window (a toggle).
// - Phaser windows already close on a backdrop tap; any of them, tracked or not, switches when
//   that backdrop tap lands on a switch button. A tap on the window itself never does.
// Phaser-free (plain objects + the scene's input events) so it runs under node --test.
import { isInputLocked } from './inputLock.js';

const BACKDROP_W = 1280, BACKDROP_H = 720;   // GAME_WIDTH × GAME_HEIGHT (config.js needs Vite)
const isBackdrop = (o) => o?.type === 'Rectangle' && o.width >= BACKDROP_W && o.height >= BACKDROP_H;

export function trackPanel(scene, id, close, { dom = false } = {}) {
  const entry = { id, close, dom };
  scene._panel = entry;
  return () => { if (scene._panel === entry) scene._panel = null; };
}

// Closes the tracked window (if any) and returns its id, e.g. before the gear opens Ustawienia.
export function closeTrackedPanel(scene) {
  const p = scene?._panel;
  if (!p) return null;
  p.close();
  return p.id;
}

export function switchButton(scene, button, id, fn) {
  const hit = button.hit ?? button;
  (scene._switches ??= []).push({ id, hit, fn });
  return { hit, onClick: () => { if (!isInputLocked(scene)) fn(); } };
}

function switchUnder(scene, pointer) {
  const live = (scene._switches ?? []).filter(({ hit }) => hit.active !== false && hit.input?.enabled
    && hit.visible !== false && hit.parentContainer?.visible !== false);
  const hits = scene.input.manager.hitTest(pointer, live.map((s) => s.hit), scene.cameras.main, []);
  return live.find((s) => hits.includes(s.hit)) ?? null;
}

// Once per scene start (Phaser drops the input listeners on shutdown).
export function installPanelSwitch(scene) {
  if (scene._panelSwitch) return;
  scene._panelSwitch = true;
  scene._switches = [];
  scene._panel = null;
  let top = null, down = null;
  scene.input.on('gameobjectdown', (_p, obj) => { top = obj; });
  scene.input.on('pointerdown', () => {
    down = isInputLocked(scene) ? { panel: scene._panel, top } : null;
  });
  // Finishes once every handler of this tap has run (a microtask): the tapped object's own
  // pointerup comes first in Phaser's order, so a window closed by its backdrop is gone and a
  // switch button's own onClick has declined; and the game's later scene-level listeners (the
  // farm's tap-to-harvest) still see the friends panel open, so a click-away tap only closes.
  scene.input.on('pointerup', (pointer) => {
    const d = down;
    down = null; top = null;
    if (!d) return;
    queueMicrotask(() => {
      const domTap = !!d.panel?.dom && scene._panel === d.panel;
      if (domTap) d.panel.close();
      else if (!isBackdrop(d.top)) return;          // the tap was on the window itself
      if (isInputLocked(scene)) return;              // something is still open
      const sw = switchUnder(scene, pointer);
      if (sw && sw.id !== d.panel?.id) sw.fn();
    });
  });
  scene.events.once('shutdown', () => { scene._panelSwitch = false; scene._switches = []; scene._panel = null; });
}
